import { useState } from 'react';
import { Dialog } from '@m/alchemy-ui/Dialog';
import { Select } from '@m/alchemy-ui/Select';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';

import { Reward, RewardTierConfiguration } from 'settings/Settings.types';
import { useSnappyCampaigns } from 'settings/hooks/useSnappyCampaigns';
import { useRewardTierConfig } from 'settings/hooks/useRewardTierConfig';

import * as Styled from './RewardsPicker.styled';
import { RewardButton } from './RewardButton';

function RewardIcons({
  loading,
  availableRewards,
  selectedRewardId,
  onClick = () => {},
}: RewardIconsProps) {
  if (loading)
    return (
      <div className="text-center margin-top-medium">
        <LoadingIndicator inline />
      </div>
    );

  return (
    <Styled.RewardIconsContainer>
      {availableRewards.map(reward => {
        const isSelected = selectedRewardId === reward.id;

        return (
          <RewardButton
            key={reward.id}
            reward={reward}
            isSelected={isSelected}
            onClick={() => onClick(reward.id)}
            className="margin-right-medium margin-bottom-medium"
          />
        );
      })}
    </Styled.RewardIconsContainer>
  );
}

interface RewardIconsProps {
  loading: boolean;
  availableRewards: Reward[];
  selectedRewardId: Reward['id'];
  onClick?: (rewardId: Reward['id']) => void;
}

export function RewardPickerDialog({
  reward,
  isOpen,
  onSave = () => {},
  onClose = () => {},
  snappyEnabled,
  multiRegionSnappyRewardsOn,
}: RewardPickerDialogProps) {
  const { campaigns } = useSnappyCampaigns();
  const { rewards, loading } = useRewardTierConfig(reward.tier);

  const defaultCampaignId = reward.campaignId || '';
  const alternativeCampaignId = reward.secondaryCampaignId || '';
  const [rewardId, setRewardId] = useState(reward.id);
  const [campaignId, setCampaignId] = useState(defaultCampaignId);
  const [secondaryCampaignId, setSecondaryCampaignId] = useState(
    alternativeCampaignId
  );

  const title = `${reward.displayName} ${snappyEnabled ? 'with Snappy' : ''}`;

  const isCampaignIdValid = !snappyEnabled || campaignId !== '';
  const isSecondaryCampaignIdValid =
    !multiRegionSnappyRewardsOn || secondaryCampaignId !== '';
  const isDirty =
    reward.id !== rewardId ||
    defaultCampaignId !== campaignId ||
    alternativeCampaignId !== secondaryCampaignId;

  const isValid = () => {
    return isDirty && isCampaignIdValid && isSecondaryCampaignIdValid;
  };

  const handleSave = () => {
    const newRewardInstance = rewards.find(reward => reward.id === rewardId) || {} as Reward;
    onSave({
      ...reward,
      ...newRewardInstance,
      campaignId: campaignId || null,
      secondaryCampaignId: secondaryCampaignId || null,
    });
  };

  if (!isOpen) return null;

  return (
    <Dialog isOpen onClose={onClose} hasCloseIcon size="large">
      <Dialog.Header isCentered>
        <h2>{title}</h2>
      </Dialog.Header>

      <Dialog.Body className="padding-medium">
        <>
          {snappyEnabled && (
            <Styled.DialogField
              required
              isFullWidth
              label="Select Snappy Campaign"
              input={
                <Select
                  value={campaignId}
                  onChange={({ value }) => setCampaignId(value)}
                  options={campaigns}
                />
              }
            />
          )}
          {snappyEnabled && multiRegionSnappyRewardsOn && (
            <Styled.DialogField
              required
              isFullWidth
              label="If Employee Locale is not United States use the following Snappy Campaign"
              input={
                <Select
                  value={secondaryCampaignId}
                  onChange={({ value }) => setSecondaryCampaignId(value)}
                  options={campaigns}
                />
              }
            />
          )}
        </>

        <Styled.DialogField
          required
          isFullWidth
          label="Choose a reward"
          input={
            <RewardIcons
              loading={loading}
              availableRewards={rewards}
              selectedRewardId={rewardId}
              onClick={rewardId => setRewardId(rewardId)}
            />
          }
        />
      </Dialog.Body>

      <Dialog.Footer
        footerButtons={[
          { priority: 'tertiary', onClick: onClose, label: 'Cancel' },
          { disabled: !isValid(), label: 'Save', onClick: handleSave },
        ]}
      />
    </Dialog>
  );
}

interface RewardPickerDialogProps {
  reward: RewardTierConfiguration;
  isOpen: boolean;
  snappyEnabled: boolean;
  multiRegionSnappyRewardsOn: boolean;
  onSave?: (newReward: RewardTierConfiguration) => void;
  onClose?: () => void;
}
